import { useEffect, useState } from 'react';
import { useLocale } from '../../context';
import { useResponsibleLimits } from '../../hooks/useResponsibleLimits';
import {
  MAX_SESSION_MINUTES,
  RESPONSIBLE_PERIODS,
  SESSION_SINGLE_KEY,
  buildEditablePeriodState,
  buildEditableSingleState,
  combineSessionHours,
  parseMinutesValue,
  splitSessionHours,
} from '../../utils/responsibleLimits';
import MobileResponsibleLimitConfirm from './MobileResponsibleLimitConfirm';
import MobileResponsibleMinutesInput from './MobileResponsibleMinutesInput';
import MobileResponsibleSessionCurrent from './MobileResponsibleSessionCurrent';
import '../../styles/registration.css';

function MobileResponsibleToggle({ checked, onChange, label }) {
  return (
    <div className="mobile-responsible-toggle">
      <div className="mobile-responsible-toggle__label">{label}</div>
      <button
        type="button"
        className={`mobile-responsible-toggle__btn${checked ? ' is-on' : ''}`}
        onClick={() => onChange(!checked)}
        role="switch"
        aria-checked={checked}
      >
        <span className="mobile-responsible-toggle__circle" />
      </button>
    </div>
  );
}

function SessionLimitRow({period, state, saving, activeCurrent, onToggle, onHoursChange, onMinutesChange, onInstall, onDelete, t: tr}) {
  const item = state[period];
  const open = item.enabled;
  const { hours, minutes } = splitSessionHours(item.value);
  const hasActive = Boolean(activeCurrent?.enabled && activeCurrent.value != null);
  const currentCombined = hasActive ? Number(activeCurrent.value) : null;
  const inputCombined = combineSessionHours(hours, minutes);
  const valueChanged = hasActive
    && inputCombined != null
    && currentCombined != null
    && Math.abs(inputCombined - currentCombined) > 0.001;
  const showDelete = hasActive && !valueChanged;
  const showInstall = !hasActive || valueChanged;

  return (
    <div className={`mobile-responsible-limit limit--RBmvG${open ? ' is-open' : ''}`}>
      <div className="mobile-responsible-limit__controls controls--V3iG4">
        <MobileResponsibleToggle
          checked={item.enabled}
          onChange={(checked) => onToggle(period, checked)}
          label={tr(`account.responsible.${period}Limit`)}
        />
      </div>
      {open && (
        <div className="mobile-responsible-limit__enter mobile-responsible-limit__enter--time enter--ZWdm4 _open">
          <div className="enter__container">
            <div className="mobile-responsible-limit__time-inputs enter__input">
              <div className="mobile-responsible-limit__time-field">
                <input
                  type="text"
                  className="mobile-responsible-limit__time-input input"
                  inputMode="numeric"
                  value={hours}
                  disabled={saving}
                  onChange={(event) => onHoursChange(period, event.target.value, minutes)}
                  autoComplete="off"
                />
                <span className="mobile-responsible-limit__time-unit">{tr('account.responsible.hoursShort')}</span>
              </div>
              <div className="mobile-responsible-limit__time-field">
                <input
                  type="text"
                  className="mobile-responsible-limit__time-input input"
                  inputMode="numeric"
                  value={minutes}
                  disabled={saving}
                  onChange={(event) => onMinutesChange(period, hours, event.target.value)}
                  autoComplete="off"
                />
                <span className="mobile-responsible-limit__time-unit">{tr('account.responsible.minutesShort')}</span>
              </div>
            </div>
            {showDelete ? (
              <button
                type="button"
                className="mobile-responsible-limit__delete enter__button"
                disabled={saving}
                onClick={() => onDelete(period)}
              >
                {tr('account.responsible.delete')}
              </button>
            ) : null}
            {showInstall ? (
              <button
                type="button"
                className="mobile-responsible-limit__install enter__button"
                disabled={saving}
                onClick={() => onInstall(period)}
              >
                {tr('account.responsible.install')}
              </button>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

function buildSingleState(limitGroup) {
  return buildEditableSingleState(limitGroup);
}

export default function MobileAccountResponsibleSession() {
  const _locale = useLocale();
  const tr = _locale.t;
  const { limits, loading, saving, msg, err, saveLimits } = useResponsibleLimits();
  const [state, setState] = useState(() => buildEditablePeriodState(limits.session));
  const [single, setSingle] = useState(() => buildSingleState(limits.session));
  const [confirmTarget, setConfirmTarget] = useState(null);

  const activeSingle = limits?.session?.current?.[SESSION_SINGLE_KEY];
  const hasActiveSingle = Boolean(activeSingle?.enabled && activeSingle.value != null);
  const currentSingleMinutes = hasActiveSingle ? Math.round(Number(activeSingle.value)) : null;
  const inputSingleMinutes = parseMinutesValue(single.value);
  const singleValueChanged = hasActiveSingle
    && inputSingleMinutes != null
    && inputSingleMinutes !== currentSingleMinutes;
  const showDelete = hasActiveSingle && !singleValueChanged;
  const showInstall = !hasActiveSingle || singleValueChanged;

  useEffect(() => {
    setState(buildEditablePeriodState(limits.session));
    setSingle(buildSingleState(limits.session));
  }, [limits.session]);

  const handleToggle = async (period, enabled) => {
    setState((current) => ({
      ...current,
      [period]: { enabled, value: enabled ? current[period].value : '' },
    }));
    if (!enabled) {
      await saveLimits('session', { [period]: { enabled: false, value: null } });
    }
  };

  const updatePeriodHours = (period, hours, minutes) => {
    const combined = combineSessionHours(hours, minutes);
    setState((current) => ({
      ...current,
      [period]: { ...current[period], value: combined != null ? String(combined) : '' },
    }));
  };

  const handleInstallPeriod = (period) => {
    const item = state[period];
    if (!item.enabled) return;
    const { hours, minutes } = splitSessionHours(item.value);
    const value = combineSessionHours(hours, minutes);
    if (!value) return;
    setConfirmTarget({ kind: 'period', period, action: 'change' });
  };

  const handleDeletePeriod = (period) => {
    setConfirmTarget({ kind: 'period', period, action: 'delete' });
  };

  const handleSingleInstallClick = () => {
    if (!single.enabled) return;
    const minutes = parseMinutesValue(single.value);
    if (!minutes || minutes > MAX_SESSION_MINUTES) return;
    setConfirmTarget({ kind: 'single', action: 'change' });
  };

  const handleSingleDeleteClick = () => {
    setConfirmTarget({ kind: 'single', action: 'delete' });
  };

  const handleConfirmAction = async () => {
    if (!confirmTarget) return;
    if (confirmTarget.kind === 'single') {
      if (confirmTarget.action === 'delete') {
        await saveLimits('session', { [SESSION_SINGLE_KEY]: { enabled: false, value: null } });
        setSingle({ enabled: false, value: '' });
      } else {
        const minutes = parseMinutesValue(single.value);
        if (!minutes || minutes > MAX_SESSION_MINUTES) {
          setConfirmTarget(null);
          return;
        }
        await saveLimits('session', { [SESSION_SINGLE_KEY]: { enabled: true, value: minutes } });
      }
    } else if (confirmTarget.action === 'delete') {
      await saveLimits('session', { [confirmTarget.period]: { enabled: false, value: null } });
      setState((current) => ({
        ...current,
        [confirmTarget.period]: { enabled: false, value: '' },
      }));
    } else {
      const item = state[confirmTarget.period];
      const { hours, minutes } = splitSessionHours(item.value);
      const value = combineSessionHours(hours, minutes);
      if (!value) {
        setConfirmTarget(null);
        return;
      }
      await saveLimits('session', { [confirmTarget.period]: { enabled: true, value } });
    }
    setConfirmTarget(null);
  };

  const handleSingleToggle = async (enabled) => {
    setSingle((current) => ({ enabled, value: enabled ? current.value : '' }));
    if (!enabled) {
      await saveLimits('session', { [SESSION_SINGLE_KEY]: { enabled: false, value: null } });
    }
  };

  const confirmTitle = confirmTarget?.action === 'delete'
    ? tr('account.responsible.confirmDeleteTitle')
    : tr('account.responsible.confirmChangeTitle');

  if (loading) {
    return <div className="mobile-responsible-panel__loading">{tr('account.loading')}</div>;
  }

  return (
    <div className="mobile-responsible-panel mobile-responsible-panel--session widget time-limits">
      <h2 className="mobile-responsible-panel__caption">{tr('account.responsible.timeLimitsTitle')}</h2>
      <p className="mobile-responsible-panel__desc">{tr('account.responsible.timeDescription')}</p>

      {err ? <div className="mobile-responsible-panel__error">{err}</div> : null}
      {msg ? <div className="mobile-responsible-panel__success">{msg}</div> : null}

      <MobileResponsibleSessionCurrent limits={limits} t={tr} />

      <div className="responsible-game-container mobile-responsible-panel__session-top">
        <div className={`mobile-responsible-limit${single.enabled ? ' is-open' : ''}`}>
          <div className="mobile-responsible-limit__controls controls--V3iG4">
            <MobileResponsibleToggle
              checked={single.enabled}
              onChange={handleSingleToggle}
              label={tr('account.responsible.sessionLimitMinutes')}
            />
          </div>
          {single.enabled && (
            <div className="mobile-responsible-limit__enter enter _opened">
              <div className="enter__container">
                <div className="enter__input">
                  <MobileResponsibleMinutesInput
                    value={single.value}
                    disabled={saving}
                    suffix={tr('account.responsible.minutesShort')}
                    onChange={(value) => setSingle((current) => ({ ...current, value }))}
                  />
                </div>
                {showDelete ? (
                  <button
                    type="button"
                    className="mobile-responsible-limit__delete enter__button"
                    disabled={saving}
                    onClick={handleSingleDeleteClick}
                  >
                    {tr('account.responsible.delete')}
                  </button>
                ) : null}
                {showInstall ? (
                  <button
                    type="button"
                    className="mobile-responsible-limit__install enter__button"
                    disabled={saving}
                    onClick={handleSingleInstallClick}
                  >
                    {tr('account.responsible.install')}
                  </button>
                ) : null}
              </div>
              <div className="enter__tip mobile-responsible-limit__tip">{tr('account.responsible.sessionMaxTip')}</div>
            </div>
          )}
        </div>
      </div>

      <div className="responsible-game-container with-self-limitation mobile-responsible-panel__limits">
        {RESPONSIBLE_PERIODS.map((period) => (
          <SessionLimitRow
            key={period}
            period={period}
            state={state}
            saving={saving}
            activeCurrent={limits.session?.current?.[period]}
            onToggle={handleToggle}
            onHoursChange={updatePeriodHours}
            onMinutesChange={(p, h, m) => updatePeriodHours(p, h, m)}
            onInstall={handleInstallPeriod}
            onDelete={handleDeletePeriod}
            t={tr}
          />
        ))}
      </div>

      <MobileResponsibleLimitConfirm
        open={Boolean(confirmTarget)}
        title={confirmTitle}
        paragraphs={[]}
        confirmLabel={tr('account.responsible.confirmSet')}
        cancelLabel={tr('account.responsible.confirmCancel')}
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmTarget(null)}
        loading={saving}
      />
    </div>
  );
}
